/**
 * Подготовка набора вопросов: ожидание, отказ, пересборка.
 *
 * `POST /brands/bootstrap` и `POST /brands/{brandId}/questions/rebuild`
 * отвечают сразу и ядра ещё не содержат: сайт читается заново, вопросы пишет
 * модель, и это до минуты внешних вызовов. Ход виден только опросом
 * `GET /brands/{brandId}` — событий кабинету никто не шлёт.
 *
 * Собрано в одном месте, потому что ждут этого два экрана: онбординг сразу
 * после заведения бренда и ядро при пересборке. Два разных текста ожидания
 * читались бы как два разных процесса, хотя процесс один и тот же.
 *
 * Отрасли здесь нет и быть не может: клиент вводит имя и адрес сайта, всё
 * остальное продукт определяет сам.
 */

import { useEffect, useRef, useState } from 'react';

import { api, requestWith } from '../api/client';
import { t } from '../i18n';
import { useAttempt, type Attempt } from './attempt';
import { Pipeline } from './state';
import { scanSteps } from './screen-states';
import { GENERATION_DURATION } from '@shared/src/product';
import type { components } from '../api/schema';

type Brand = components['schemas']['Brand'];

/** Интервал опроса по умолчанию — три секунды, как у прогонов (docs/API.md §8). */
const POLL_FALLBACK = 3;
// Потолок паузы между попытками после сетевой ошибки. Полминуты — это ещё
// «экран сам оживёт», но уже не опрос лежащего сервера каждые три секунды.
const POLL_MAX = 30;

/**
 * Шаги подготовки. Замера среди них нет намеренно: заведение бренда прогон
 * больше не ставит в очередь — спрашивать движки нечем, пока набора нет.
 */
export function prepareSteps(): string[] {
  return scanSteps().slice(0, 2);
}

/** Идёт ли подготовка прямо сейчас. Опрашивать имеет смысл только это состояние. */
export function isPreparing(brand: Brand | null | undefined): boolean {
  return brand?.preparation_state === 'preparing';
}

/**
 * Опрос подготовки до её конца.
 *
 * Устроен как опрос прогона: интервал задаёт сервер заголовком `Retry-After`,
 * а `brandId = null` означает «ждать нечего» и опрос не запускает. Терминальный
 * бренд уезжает в `onSettled` один раз — экран сам решает, что с ним делать:
 * онбординг уводит в карточку, ядро перечитывает список.
 */
export function usePreparation(
  brandId: string | null,
  onSettled: (brand: Brand) => void,
): { error: unknown } {
  const [answer, setAnswer] = useState<{ id: string; error: unknown } | null>(null);

  // Обработчик завершения пересоздаётся каждой перерисовкой, и держать его в
  // зависимостях эффекта значит перезапускать опрос каждые три секунды.
  const settled = useRef(onSettled);
  useEffect(() => {
    settled.current = onSettled;
  });

  useEffect(() => {
    if (!brandId) return;

    const abort = new AbortController();
    let timer: number | undefined;
    let attempt = 0;

    const tick = () => {
      requestWith<Brand>(`/brands/${brandId}`, { signal: abort.signal })
        .then(({ body, retryAfter }) => {
          // Удачный ответ ничего на экране не меняет, пока подготовка идёт:
          // новое состояние здесь означало бы перерисовку всего экрана каждые
          // три секунды на ровном месте.
          setAnswer((prev) =>
            prev?.id === brandId && prev.error === null
              ? prev
              : { id: brandId, error: null },
          );
          if (body.preparation_state === 'preparing') {
            attempt = 0;
            timer = window.setTimeout(tick, (retryAfter ?? POLL_FALLBACK) * 1000);
          } else {
            settled.current(body);
          }
        })
        .catch((error: unknown) => {
          if (error instanceof DOMException && error.name === 'AbortError') return;
          setAnswer({ id: brandId, error });
          // ОПРОС ПРОДОЛЖАЕТСЯ ПОСЛЕ ОШИБКИ. Оборванная сеть — обычное дело, а
          // подготовка идёт на сервере и об экране не знает: остановившись на
          // первой неудаче, экран навсегда оставался бы в «Готовим вопросы» с
          // закрытыми действиями, хотя набор давно написан. Пауза растёт до
          // предела, чтобы лежащий сервер не получал опрос каждые три секунды.
          attempt += 1;
          const backoff = Math.min(POLL_FALLBACK * 2 ** attempt, POLL_MAX);
          timer = window.setTimeout(tick, backoff * 1000);
        });
    };

    tick();
    return () => {
      abort.abort();
      if (timer) window.clearTimeout(timer);
    };
  }, [brandId]);

  // Ошибка, пойманная на другом бренде, — это ошибка про другой экран.
  return { error: answer?.id === brandId ? answer.error : null };
}

/**
 * Ожидание набора. Счётчика у подготовки нет — значит шаги конвейера, а не
 * неопределённый индикатор (app-kit.md §5).
 */
export function Preparing({ planned }: { planned?: number }) {
  return (
    <div className="running" role="status">
      <p className="stamp">
        {t('prepare.body', { planned: planned ?? 0, duration: GENERATION_DURATION })}
      </p>
      <Pipeline steps={prepareSteps()} current={0} />
    </div>
  );
}

/**
 * Подготовка не удалась.
 *
 * Причину пишет сервер (`preparation_note`) — он знает, на чём споткнулся:
 * сайт не открылся, домена нет, модель не ответила. Своей догадки у экрана нет
 * и быть не должно.
 */
export function PreparationFailed({
  note,
  busy,
  onRetry,
}: {
  note?: string;
  /** Пересборка уже отправлена: повторное нажатие сервер отобьёт как `409`. */
  busy: boolean;
  onRetry?: () => void;
}) {
  return (
    <div className="notice warn" role="status">
      <b>{t('prepare.failed')}</b>
      {noteProse(note)}
      {onRetry ? (
        <span className="notice-act">
          <button
            className="btn btn-ghost btn-sm"
            type="button"
            disabled={busy}
            onClick={onRetry}
          >
            {busy ? t('prepare.retrying') : t('prepare.retry')}
          </button>
        </span>
      ) : null}
    </div>
  );
}

/**
 * Причина отказа человеческим текстом.
 *
 * То же правило, что у `scan_blocked_reason` в `screen-states.tsx`: поле
 * свободное, и в него попадает как фраза для клиента, так и машинный код от
 * воркера. Код показывать нечего — он ничего не объясняет и выглядит как
 * поломка продукта.
 */
function noteProse(note?: string): string {
  if (!note) return t('prepare.note-fallback');
  const looksLikeCode = !/\s/.test(note) && !/[а-яё]/i.test(note);
  return looksLikeCode ? t('prepare.note-fallback') : note;
}

/**
 * Пересобрать набор заново.
 *
 * Ключ идемпотентности обязателен: подготовка читает сайт и платит за вызовы
 * модели, и повтор запроса из-за оборвавшейся сети не должен стоить второго
 * набора.
 */
export function rebuildQuestions(brandId: string, key: string): Promise<Brand> {
  return api.post<Brand>(`/brands/${brandId}/questions/rebuild`, undefined, {
    idempotencyKey: key,
  });
}

/**
 * Ключ ОДНОЙ попытки пересборки, переживающий неопределённый сетевой исход.
 *
 * Новый ключ на каждый вызов защищал только от двойного нажатия. Настоящий
 * опасный случай другой: сервер запрос принял, а ответ до нас не доехал. Повтор
 * с новым ключом уходит как ВТОРАЯ пересборка — и, если первая успела
 * закончиться, второй раз заменяет ядро, стирая набор, который клиент уже видел.
 * Один ключ на попытку делает повтор безопасным: сервер узнаёт его и вернёт тот
 * же ответ.
 *
 * Сама механика лежит в `ui/attempt.ts`: она нужна ещё и заведению клиента
 * агентства, а два одинаковых `useState(idempotencyKey)` в разных файлах — это
 * два места, где однажды поправят только одно.
 */
export function useRebuildAttempt(): Attempt {
  return useAttempt();
}
